import useApi from "@/hooks/useApi";
import { renderMarkdown } from "@/pages/Billing/markdownUtils";
import * as React from "react";
import { useCallback, useState } from "react";
import SurveyEditor from "./SurveyEditor";
import SurveyTemplateWizard from "./SurveyTemplateWizard";
import AiSurveyGenerateModal from "./AiSurveyGenerateModal";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import {
  IconPlus,
  NetPromoterScoreInput,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity,
  StarRatingInput,
  Survey,
  SurveyQuestion
} from "@velaro/velaro-component-lib";
import Select from "@/components/Select";
import useSurveys from "@/hooks/useSurveys";
import { languageOptionsList } from "@/constants/languages";

function defaultSurvey(): Survey {
  return {
    id: 0,
    name: "New Survey",
    questions: []
  };
}

function tryParseJson(val: any): any {
  if (val == null || typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

function serializeSurveyForPost(survey: any): any {
  return {
    ...survey,
    questions: Array.isArray(survey.questions)
      ? survey.questions.map((q: any) => ({
          ...q,
          visibilityCondition:
            q.visibilityCondition != null && typeof q.visibilityCondition === "object"
              ? JSON.stringify(q.visibilityCondition)
              : q.visibilityCondition ?? null,
          hiddenConfig:
            q.hiddenConfig != null && typeof q.hiddenConfig === "object"
              ? JSON.stringify(q.hiddenConfig)
              : q.hiddenConfig ?? null,
          groupConfig:
            q.groupConfig != null && typeof q.groupConfig === "object"
              ? JSON.stringify(q.groupConfig)
              : q.groupConfig ?? null,
        }))
      : [],
  };
}

function deserializeSurvey(survey: any): any {
  return {
    ...survey,
    questions: Array.isArray(survey.questions)
      ? survey.questions.map((q: any) => ({
          ...q,
          visibilityCondition: tryParseJson(q.visibilityCondition),
          hiddenConfig: tryParseJson(q.hiddenConfig),
          groupConfig: tryParseJson(q.groupConfig),
        }))
      : [],
  };
}

export default function Surveys() {
  const api = useApi();
  const { surveys, setSurveys } = useSurveys();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const survey = surveys[selectedIndex];
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>[]>([]);
  const toastAlert = useToastAlert();
  const [dirty, setDirty] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showAiWizard, setShowAiWizard] = useState(false);
  const { subscription } = useSubscriptions();
  const aiEnabled = !!subscription?.enableAiSurveyBuilder;
  const [netSuiteMappings, setNetSuiteMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [listFields, setListFields] = useState([]);

  const save = useCallback(async () => {
    if (!survey) {
      return;
    }
    let errors: Record<string, string>[] = [];
    setErrors(errors);

    survey.questions.forEach((q, i) => {
      const type = q.type as string;
      if (type === "pageBreak" || type === "hidden") return; // no text needed
      if (!q.text) {
        const clone = [...errors];
        clone[i] = { text: "Question text is required" };
        setErrors(clone);
        errors = clone;
      }
    });
    if (errors.length > 0) {
      return;
    }

    setSaving(true);
    const resp = await api.messaging.post("Surveys", serializeSurveyForPost(survey));
    if (resp.ok) {
      toastAlert.displayToast(Severity.Success, "Survey saved successfully");
      setDirty(false);
      const savedSurvey = deserializeSurvey(await resp.json());
      const clone = [...surveys];
      clone[selectedIndex] = savedSurvey;
      setSurveys(clone);
    } else {
      toastAlert.displayToast(Severity.Error, "Failed to save survey");
    }
    setSaving(false);
  }, [api.messaging, selectedIndex, setSurveys, survey, surveys, toastAlert]);

  const remove = useCallback(async () => {
    if (!survey) {
      return;
    }
    const clone = [...surveys];
    clone.splice(selectedIndex, 1);
    setSurveys(clone);
    setSelectedIndex(0);

    setSaving(true);
    const resp = await api.messaging.delete("Surveys", survey);
    if (resp.ok) {
      toastAlert.displayToast(Severity.Success, "Survey deleted successfully");
    } else {
      toastAlert.displayToast(Severity.Error, "Failed to delete survey");
    }
    setSaving(false);
  }, [api.messaging, selectedIndex, setSurveys, survey, surveys, toastAlert]);

  const fetchNetSuiteFields = useCallback(async () => {
    try {
      setLoadingMappings(true);
      const response = await api.messaging.get("Surveys/netsuite/mappings");
      if (response.ok) {
        const mappings = await response.json();
        console.log("NetSuite mappings loaded:", mappings);
        setNetSuiteMappings(mappings);
      } else {
        console.error("Failed to fetch NetSuite mappings");
        setNetSuiteMappings([]);
      }
    } catch (error) {
      console.error("Error fetching NetSuite mappings:", error);
      setNetSuiteMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  }, [api.messaging]);

  React.useEffect(() => {
    fetchNetSuiteFields();
  }, [fetchNetSuiteFields]);

  const fetchListFields = useCallback(
    async (netSuiteFields: any[]) => {
      if (!netSuiteFields || netSuiteFields.length === 0) return;

      try {
        const response = await api.messaging.post(
          "Surveys/netsuite/listfields",
          netSuiteFields
        );
        if (response.ok) {
          const fields = await response.json();
          setListFields(fields);
        }
      } catch (error) {
        console.error("Error fetching list fields:", error);
      }
    },
    [api.messaging]
  );

  React.useEffect(() => {
    if (netSuiteMappings.length > 0) {
      fetchListFields(netSuiteMappings);
    }
  }, [netSuiteMappings, fetchListFields]);

  return (
    <>
      {showWizard && (
        <SurveyTemplateWizard
          onApply={(newSurvey) => {
            setSurveys([...surveys, newSurvey]);
            setSelectedIndex(surveys.length);
            setDirty(true);
            setShowWizard(false);
          }}
          onClose={() => setShowWizard(false)}
        />
      )}
      {showAiWizard && (
        <AiSurveyGenerateModal
          onApply={(newSurvey) => {
            setSurveys([...surveys, deserializeSurvey(newSurvey)]);
            setSelectedIndex(surveys.length);
            setDirty(true);
            setShowAiWizard(false);
          }}
          onClose={() => setShowAiWizard(false)}
        />
      )}
      <div className="panel bg-white dark:bg-stone-300 rounded-md shadow-md mb-4 p-4 flex flex-col gap-4">
        {survey && (
          <Select
            options={surveys.map((s, i) => ({ label: s.name, value: i }))}
            value={selectedIndex}
            onChange={(index) => setSelectedIndex(Number(index))}
          />
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <PrimaryBlueButton
            prefixIcon={<IconPlus />}
            label="New Survey"
            onClick={() => {
              setSurveys([...surveys, defaultSurvey()]);
              setSelectedIndex(surveys.length);
            }}
          />
          <SecondaryBlueButton
            prefixIcon={
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
            }
            label="Start from template"
            onClick={() => setShowWizard(true)}
          />
          <button
            type="button"
            onClick={() => aiEnabled && setShowAiWizard(true)}
            disabled={!aiEnabled}
            title={aiEnabled ? "Describe a goal — AI drafts the questions" : "Pro feature — upgrade to enable"}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border ${
              aiEnabled
                ? "border-primary text-primary hover:bg-primary hover:text-white cursor-pointer"
                : "border-slate-200 text-slate-400 cursor-not-allowed bg-slate-50"
            }`}
          >
            {!aiEnabled && (
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            )}
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5L10 2z" />
            </svg>
            Generate with AI
          </button>
          {dirty && (
            <div className="ml-auto">
              <PrimaryBlueButton
                label={saving ? "Saving..." : "Save"}
                onClick={save}
                disabled={saving}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-4">
        <div className="panel bg-white dark:bg-stone-300 rounded-md shadow-md mb-4 p-4 flex-auto">
          {!survey && (
            <div>You have no surveys. Start by creating a new one.</div>
          )}
          {survey && (
            <div className="overflow-auto">
              <SurveyEditor
                errors={errors}
                survey={survey!}
                netSuiteMappings={netSuiteMappings}
                loadingMappings={loadingMappings}
                update={(value) => {
                  const clone = [...surveys];
                  clone[selectedIndex] = value;
                  setSurveys(clone);
                  setDirty(true);
                }}
                remove={remove}
              />
            </div>
          )}
        </div>
        {survey && (
          <div className="panel bg-white dark:bg-stone-300 rounded-md shadow-md mb-4 p-4 flex-auto">
            {((survey as any).formHeading || (survey as any).formSubheading || (survey as any).formPurpose) && (
              <div className="mb-4 pb-4 border-b border-slate-100">
                {(survey as any).formHeading && (
                  <h2 className="text-lg font-bold text-slate-800">{(survey as any).formHeading}</h2>
                )}
                {(survey as any).formSubheading && (
                  <p className="text-slate-600 mt-1">{(survey as any).formSubheading}</p>
                )}
                {(survey as any).formPurpose && (
                  <p className="text-sm text-slate-500 mt-1">{(survey as any).formPurpose}</p>
                )}
              </div>
            )}
            {survey.questions.map((q, i) => (
              <QuestionPreview
                key={i}
                question={q}
                netSuiteMappings={netSuiteMappings}
                listFields={listFields}
              />
            ))}
            {(survey as any).consentEnabled && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                  {(survey as any).consentText
                    ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown((survey as any).consentText) }} />
                    : <span className="italic text-slate-400">Consent text will appear here</span>
                  }
                  {(survey as any).consentType === "explicit" && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" disabled />
                      <span>I agree</span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function QuestionPreview({
  question,
  netSuiteMappings = [],
  listFields = []
}: {
  question: SurveyQuestion;
  netSuiteMappings?: any[];
  listFields?: any[];
}) {
  const label = question.required ? `* ${question.text}` : question.text;
  const options = question.options || [];

  function renderInput() {
    switch (question.type as string) {
      case "name":
      case "email":
      case "text":
        return <input type="text" className={`input w-full`} />;
      case "textArea":
        return <textarea className={`input w-full`} />;
      case "choice": {
        if ((question as any).displayMode === "radio") {
          return (
            <div className="flex flex-col gap-1.5">
              {options.map((o, i) => (
                <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name={`preview-choice-${i}`} value={o} readOnly className="accent-primary" />
                  {o}
                </label>
              ))}
            </div>
          );
        }
        return (
          <select className={`input w-full`}>
            <option value={""}>--select an option--</option>
            {options.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      }
      case "checkbox": {
        return options.map((o, i) => (
          <div key={i}>
            <label className="flex gap-2">
              <input type="checkbox" />
              {o}
            </label>
          </div>
        ));
      }
      case "star":
        return <StarRatingPreview />;
      case "netPromoterScore":
        return <NetPromoterScoreInputPreview />;
      case "crmField":
        return <CrmFieldPreview question={question} listFields={listFields} />;
      case "language": {
        const languagesToShow =
          options.length > 0 ? options : languageOptionsList;
        return (
          <select className={`input w-full`}>
            <option value={""}>--select a language--</option>
            {languagesToShow.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      }
      case "select":
        return (
          <select className="input w-full">
            <option value="">--select an option--</option>
            {options.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      case "multiSelect":
        return (
          <select className="input w-full" multiple size={Math.min(options.length || 3, 5)} aria-label="Multi-select options">
            {options.map((o, i) => (
              <option key={i} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      case "pageBreak":
        return (
          <div className="border-t-2 border-dashed border-slate-300 my-2 flex items-center gap-2">
            <span className="text-xs text-slate-400 bg-white px-2">page break</span>
          </div>
        );
      case "visitorPhone":
        return (
          <div className="flex gap-2">
            <select className="input w-24" aria-label="Country code">
              <option>+1 🇺🇸</option>
            </select>
            <input type="tel" className="input flex-1" placeholder="(555) 000-0000" />
          </div>
        );
      case "tags":
        return (
          <div className="flex flex-wrap gap-1">
            {(options.length > 0 ? options : ["Support", "Billing", "Sales"]).map((o, i) => (
              <label key={i} className="flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 text-xs cursor-pointer">
                <input type="checkbox" className="w-3 h-3" />
                {o}
              </label>
            ))}
          </div>
        );
      case "hidden":
        return (
          <div className="flex items-center gap-2 bg-amber-50 border border-dashed border-amber-300 rounded px-3 py-2 text-xs text-amber-700">
            <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Hidden — not shown to visitor
          </div>
        );
      case "group":
        return (
          <select className="input w-full">
            <option value="">--select a team--</option>
            <option disabled>(Teams loaded at runtime)</option>
          </select>
        );
      case "groupRadio":
        return (
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-2 text-sm text-slate-400 italic">
              <input type="radio" disabled />
              (Teams loaded at runtime)
            </label>
          </div>
        );
      case "label":
      default:
        return null;
    }
  }

  if ((question.type as string) === "pageBreak") {
    return (
      <div className="mb-3">
        {renderInput()}
      </div>
    );
  }

  if ((question.type as string) === "hidden") {
    return (
      <div className="mb-3">
        <label className="flex left-0 text-xs text-slate-400 italic">
          Hidden: {question.variable || "unnamed"}
        </label>
        {renderInput()}
      </div>
    );
  }

  return (
    <div className="mb-3">
      <label className="flex left-0 text-black">{label}</label>
      {renderInput()}
    </div>
  );
}

function StarRatingPreview() {
  const [value, setValue] = useState(0);
  return <StarRatingInput value={value} onChange={setValue} />;
}

function NetPromoterScoreInputPreview() {
  const [value, setValue] = useState(0);
  return <NetPromoterScoreInput value={value} onChange={setValue} />;
}

function CrmFieldPreview({
  question,
  listFields
}: {
  question: SurveyQuestion;
  listFields?: any;
}) {
  let relevantListItems: any[] = [];

  // Check if we have the crmOptions metadata and listFields data
  if (question.crmOptions && listFields && typeof listFields === "object") {
    const { recordType, netSuiteField } = question.crmOptions;

    if (
      recordType &&
      netSuiteField &&
      listFields[recordType] &&
      listFields[recordType][netSuiteField]
    ) {
      const fieldOptions = listFields[recordType][netSuiteField];
      if (Array.isArray(fieldOptions)) {
        relevantListItems = fieldOptions.map((option) => ({
          value: option.value,
          label: option.text || option.label || option.value
        }));
      }
    }
  }

  return (
    <select className={`input w-full`}>
      <option value="">--select an option--</option>
      {relevantListItems.map((item, i) => (
        <option key={i} value={item.value || ""}>
          {item.label || "Unknown"}
        </option>
      ))}
    </select>
  );
}
